import { addDays, endOfDay, isBefore, isWithinInterval, parseISO, startOfDay, subMonths, subWeeks } from 'date-fns';

export const DateUtils = {
  /**
   * Check if a date is null or undefined (no date)
   */
  isNoDate: (date: string | null | undefined): boolean => {
    return !date;
  },

  /**
   * Check if a date is overdue (before today)
   */
  isOverDue: (date: string | null | undefined): boolean => {
    if (!date) return false;
    try {
      const dueDate = typeof date === 'string' ? parseISO(date) : date;
      const today = startOfDay(new Date());
      return isBefore(startOfDay(dueDate), today);
    } catch {
      return false;
    }
  },

  /**
   * Check if a date is due within the next N days (default: 7 days)
   */
  isDueInNextDays: (date: string | null | undefined, days: number = 7): boolean => {
    if (!date) return false;
    try {
      const dueDate = typeof date === 'string' ? parseISO(date) : date;
      const today = startOfDay(new Date());
      const nextDaysDate = endOfDay(addDays(today, days));
      const normalizedDueDate = startOfDay(dueDate);

      return isWithinInterval(normalizedDueDate, { start: today, end: nextDaysDate });
    } catch {
      return false;
    }
  },

  /**
   * Check if a date was due in the last N weeks (default: 2 weeks)
   */
  isDueInLastWeeks: (date: string | null | undefined, weeks: number = 2): boolean => {
    if (!date) return false;
    try {
      const dueDate = typeof date === 'string' ? parseISO(date) : date;
      const today = endOfDay(new Date());
      const lastWeeksDate = startOfDay(subWeeks(today, weeks));
      const normalizedDueDate = startOfDay(dueDate);

      return isWithinInterval(normalizedDueDate, { start: lastWeeksDate, end: today });
    } catch {
      return false;
    }
  },

  /**
   * Check if a date was due in the last N months (default: 3 months)
   */
  isDueInLastMonths: (date: string | null | undefined, months: number = 3): boolean => {
    if (!date) return false;
    try {
      const dueDate = typeof date === 'string' ? parseISO(date) : date;
      const today = endOfDay(new Date());
      const lastMonthsDate = startOfDay(subMonths(today, months));
      const normalizedDueDate = startOfDay(dueDate);

      return isWithinInterval(normalizedDueDate, { start: lastMonthsDate, end: today });
    } catch {
      return false;
    }
  },

  /**
   * Check if a date is due within a specific timeframe (between start and end dates)
   */
  isDueInTimeframe: (
    date: string | null | undefined,
    startDate: string | Date | null | undefined,
    endDate: string | Date | null | undefined
  ): boolean => {
    if (!date || !startDate || !endDate) return false;
    try {
      const dueDate = typeof date === 'string' ? parseISO(date) : date;
      const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
      const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;

      const normalizedDueDate = startOfDay(dueDate);
      const normalizedStart = startOfDay(start);
      const normalizedEnd = endOfDay(end);

      return isWithinInterval(normalizedDueDate, { start: normalizedStart, end: normalizedEnd });
    } catch {
      return false;
    }
  },

  /**
   * Get the timezone of the user
   */
  getTimezone: (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  },
};
